import { useState } from "react";

import { DocumentsIcon } from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import { getFileMetadata } from "@transfr-inc/dashboard-components/utils";

import { useContainer } from "@contexts/container-context";
import { useCurrentUser } from "@queries";
import { OohStatus, MasteryStatus } from "@models";
import { ContentType } from "@transfr-inc/dashboard-components/curriculum";
import { ActivityStatusNotification } from "@views/my-work/components/course/activity-status-notification";

import "./curriculum-resource-activity-view.scss";

const OpenLabelByType = {
  pdf: "Open PDF",
  presentation: "Open Slides",
  spreadsheet: "Open Sheet",
  video: "Open Video",
  image: "Open Image",
  form: "Open Form",
};

const CurriculumResourceActivityView = ({
  data,
  courseId,
  classroomId,
  lessonId: lessonIdProp,
  unitId: unitIdProp,
  onMarkDone,
}) => {
  const { insightsService } = useContainer();
  const currentUser = useCurrentUser();

  const {
    id,
    title,
    description,
    masteryStatus,
    isComplete,
    latestTimestamp,
    hostedContentPath,
    mimeType,
    lessonId: lessonIdFromData,
    unitId: unitIdFromData,
  } = data || {};

  const [marking, setMarking] = useState(false);
  const [localIsDone, setLocalIsDone] = useState(!!isComplete);

  const lessonId = lessonIdProp ?? lessonIdFromData;
  const unitId = unitIdProp ?? unitIdFromData;

  const isExempt = masteryStatus === MasteryStatus.Exempt;
  const isDone = localIsDone || isComplete || isExempt;

  const { type } = getFileMetadata(mimeType);
  const openLabel = OpenLabelByType[type] ?? "Open Document";

  const handleOpen = async () => {
    window.open(hostedContentPath, "_blank", "noopener,noreferrer");

    // Guard: onMarkDone triggers a recursive children refresh in the parent. Removing
    // this check would cause infinite re-creation of the children closure.
    if (!isDone && !marking) {
      setMarking(true);
      setLocalIsDone(true);
      onMarkDone?.();
      try {
        await insightsService.updateUserActivityMetrics(
          courseId,
          classroomId,
          currentUser?.userId,
          {
            activityId: id,
            lessonId,
            unitId,
            contentType: "curriculumResource",
            isComplete: true,
            completionStatus: OohStatus.COMPLETED,
            isAttempt: true,
          }
        );
      } finally {
        setMarking(false);
      }
    }
  };

  return (
    <div className="cr-main-container">
      <div className="cr-content-container">
        <div className="cr-left-container">
          <ActivityStatusNotification
            status={isExempt ? MasteryStatus.Exempt : isDone ? OohStatus.COMPLETED : ""}
            contentType={ContentType.CurriculumResources.id}
            latestTimestamp={latestTimestamp}
          />
          <div className="activity-title">{title}</div>
          {description && (
            <div className="activity-description">{description}</div>
          )}
        </div>
        <div className="cr-right-container">
          <div className="cr-document-visual">
            <div className="cr-document-header" />
            <div className="cr-document-lines">
              <div className="cr-document-line cr-document-line--title" />
              <div className="cr-document-line" />
              <div className="cr-document-line" />
              <div className="cr-document-line cr-document-line--short" />
            </div>
          </div>
          <div className="cr-button-container">
            <Button
              className="cr-open-button"
              onClick={handleOpen}
              disabled={marking || isExempt}
            >
              <DocumentsIcon
                className="cr-open-button-icon"
                mimeType={mimeType}
              />
              {isDone ? openLabel : "Open to Complete"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurriculumResourceActivityView;
